<template lang="pug">
    .play-box.puzzle-playbox(v-show='!isFirstTime', :class="{'puzzle-playbox_with-loader': isLoading}", ref="playBox")
        template(v-if='!isLoading')
            game-header(
                v-if="!isBattle",
                @soundStateChanged="headerKey = !headerKey",
                :key="headerKey",
                slug="LonerBonusGames",
                :games-type="courseType"
                :is-homework="!!exerciseId",
            )
            .loner_game-wrapper(v-show='!gameEnd')
                #loner.loner-game-canvas-handler
                .game-info__container(ref="info")
                    .game-info__block
                        game-info(
                            :score="{left: !isBattle ? answers.good : userScore, right: !isBattle ? answers.bad : opponentScore}"
                            :time="getTime"
                            :is-olympiad="isOlympiad"
                            :is-from-battle="isBattle",
                            :opponent="opponent",
                            :is-opponent-answering="isOpponentAnswering",
                            :parameters="parameters",
                            :game-allows-tips="true",
                            :is-need-to-use-default-position-for-tips-block="isHomework"
                        )
                        cs-button.answer-button(
                                color="blue",
                                pattern="primary-outline",
                                type='button',
                                @click='submitAnswer',
                                :disabled="!selectedItem"
                            )
                            | {{ $i18n.t('trainer.trainer_gamePlayBox_reply') }}
            .puzzle-playbox__result-container(v-if='gameEnd')
                img.loner-playbox__result-image(:src='searchedItem')
                result-buttons(
                    :exercise-id="exerciseId"
                    :games-type="courseType"
                )
                slot
                cs-button.play-box__result-button(v-if="possibleToContinueGame", @click="gameContinuationHandler(false)", type="button", color="blue")
                        | {{ $i18n.t('trainer.trainer_gamePlayBox_playOn') }}
                cs-button(v-if="!possibleToContinueGame && !exerciseId", @click="gameContinuationHandler(true)").play-box__result-button(type="button", color="blue")
                        | {{ $i18n.t('trainer.trainer_gamePlayBox_restart') }}
        calculating-answers(v-if="calculatingAnswers")
</template>

<script>
    import GameHeader from '../../../trainer/GameHeader.vue';
    import RotationIcon from '../../../motivation/detective/detective-assets/RotationIcon.vue';
    import GameInfo from '../../../trainer/games/GameInfo.vue';

    import trainerConfig from '../../../../config/trainer';
    import gamesConfig from '../../../../config/games';
    import timer from '../../../../utils/trainer/timer';
    import getBase64FromUrl from '../../../../utils/getBase64FromUrl';

    import { cloneDeep, kebabCase, debounce } from 'lodash';
    import { mapState } from 'vuex';
    import Fireworks from '../../../trainer/games/Fireworks.vue';
    import GameStatsApi from '../../../../api/games/gameStat';
    import ResultButtons from '../../../trainer/games/ResultButtons.vue';
    import Battle from '../../../../utils/battle/battleMixin';
    import GameMixin from '../../../../utils/trainer/game';
    import answerDurationMixin from '../../../../utils/trainer/answerDurationMixin';
    import EndGameMixin from '../../../../utils/trainer/endGame';
    import CalculatingAnswers from '../../../trainer/CalculatingAnswersModal.vue';
    import LonerBonusGamesApi from '../../../../api/games/lonerBonusGamesApi';
    import CsButton from '../../../../elements/CsButton';
    import MobileDetect from 'mobile-detect/mobile-detect.js';
    import { windowDimensions } from '../../../../utils/windowDimensions';

    const ANIMATION_TIME = 1500;
    const DEFAULT_VOLUME = 0.6;

    export default {
        props: {
            exerciseId: [Number, String],
            playersNumber: Number,
            showTurtle: {
                type: Boolean,
                default: true,
            },
            gameType: {
                type: String,
                default: 'homework',
            },
            answerDuration: Number,
            checkMultiplayerAnswers: Boolean,
            playAgain: Boolean,
            isLastGameInBattle: {
                type: Boolean,
                default: false,
            },
        },
        components: {
            CsButton,
            ResultButtons,
            RotationIcon,
            GameHeader,
            GameInfo,
            CalculatingAnswers,
            Fireworks,
        },
        mixins: [timer, getBase64FromUrl, GameMixin, answerDurationMixin, EndGameMixin, Battle],
        data() {
            return {
                parameters: null,
                gameStat: null,
                isFirstTime: true,
                imageSize: null,
                stage: null,
                container: null,
                frame: null,
                gameItems: [],
                searchedItem: null,
                isCorrectImageChosen: false,
                selectedItem: null,
                gameEnd: false,
                isLoading: true,
                loadedSound: 0,
                trainerConfig: cloneDeep(trainerConfig),
                gamesConfig: cloneDeep(gamesConfig),
                soundInstance: null,
                infinityModeCompleted: false,
                answers: {
                    good: 0,
                    bad: 0,
                },
                initialParameters: null,
                headerKey: true,
                lonerData: null,
                isMobile: false,
                isTablet: false,
                isVerticalScreen: false,
                pregGroup: null,
                documentElementWidth: 0,
                documentElementHeight: 0,
                maxDifficulty: 5,
            };
        },
        created() {
            window.addEventListener('beforeunload', this.beforeTabClosed);
            window.addEventListener('resize', this.onResize);
            this.$nextTick(this.onResizeImmediately);
        },
        async mounted() {
            this.isMobile = Boolean(new MobileDetect(window.navigator.userAgent).phone());
            this.isTablet = Boolean(new MobileDetect(window.navigator.userAgent).tablet());
            await Promise.all([this.getMaxDifficulty(), this.fetchData()]);
            this.parameters = cloneDeep(this.parametersForLoner);
            this.initialParameters = cloneDeep(this.parametersForLoner);
            this.parameters.slug = this.$attrs.type;
            if (this.parameters.complication && this.gameStat?.answers) {
                if (this.parameters.itemsAmount + this.gameStat?.answers < this.maxDifficulty) {
                    this.parameters.itemsAmount += +this.gameStat?.answers || 0;
                } else {
                    this.parameters.itemsAmount = this.maxDifficulty;
                }
            }
            this.fetchImages();
            this.registerSounds();
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.onResize);
            this.$createjs.Sound.removeAllSounds();
            this.$createjs.Sound.removeAllEventListeners();
            this.infinityModeCompleted = true;
            delete this.frame;
            delete this.container;
            delete this.stage;
            this.beforeTabClosed();
            window.removeEventListener('beforeunload', this.beforeTabClosed);
        },
        methods: {
            onResizeDebounce: debounce(function() {
                this.initCanvas();
            }, 50),
            onResizeImmediately() {
                const data = windowDimensions();
                this.isVerticalScreen = data.isVerticalScreen;
                this.documentElementWidth = document.documentElement.clientWidth;
                this.documentElementHeight = document.documentElement.clientHeight;
                this.removeLonerCanvas();
            },
            onResize() {
                this.onResizeImmediately();
                this.onResizeDebounce();
            },
            removeLonerCanvas() {
                const lonerCanvasList = this.$refs.playBox.querySelectorAll('#lonerCanvas');
                lonerCanvasList.forEach(e => {
                    e.remove();
                });
            },
            createStat() {
                this.parameters = cloneDeep(this.parametersForLoner);
                let stat = {
                    parameters: this.parameters,
                    game: this.$attrs.type,
                    course: kebabCase(this.courseType),
                    userId: this.user.id,
                    attempts: 0,
                    gameExerciseId:
                        this.exercise && this.exercise.sourceType === 'olympiadLevelTrainerStage'
                            ? undefined
                            : this.exerciseId,
                };
                return GameStatsApi.create(stat);
            },
            gameContinuationHandler(isRestartingGame) {
                if (isRestartingGame) {
                    this.parameters = this.initialParameters;
                    this.resetTimer();
                    this.answers.good = 0;
                    this.answers.bad = 0;
                    this.fetchData();
                    this.createStat();
                }
                this.gameEnd = false;
                this.selectedItem = null;
                this.searchedItem = null;
                this.container.children.forEach(child => {
                    child.removeEventListener('click');
                });
                this.container.removeAllChildren();
                this.stage.removeAllChildren();
                this.container = null;
                this.stage = null;
                this.frame = null;
                const oldCanvas = document.getElementById('loner');
                oldCanvas.textContent = '';
                if (this.parameters.complication && this.parameters.itemsAmount !== this.maxDifficulty) {
                    this.parameters.itemsAmount++;
                }
                this.fetchImages();
            },
            beforeTabClosed() {
                if (
                    (this.startGame || (!this.startGame && !this.gameEnd && this.correct !== null)) &&
                    this.user.type === 'student' &&
                    !this.isBattle
                ) {
                    this.refreshGameStatBeforeSend();
                    this.exerciseId ? this.sendStat(false) : this.sendStat(true);
                    this.gameEnd = true;
                }
                return null;
            },
            wrongImageSubmitHandler() {
                this.drawTileShape(this.selectedItem.shape, this.selectedItem.bitmap, 'red');
                this.stage.update();
                this.answers.bad++;
            },
            selectImageHandler(bitmap, isSearchedItem, shape) {
                this.selectedItem && this.drawTileShape(this.selectedItem.shape, this.selectedItem.bitmap);
                this.selectedItem = {
                    bitmap,
                    shape,
                };
                this.drawTileShape(shape, bitmap, 'green');
                this.stage.update();
                this.isCorrectImageChosen = isSearchedItem;
            },
            submitAnswer() {
                if (this.isCorrectImageChosen === true) {
                    this.answers.good++;
                    this.refreshGameStatBeforeSend();
                    this.gameEnd = true;
                    if (this.parameters.infinityAmountOfExamples) {
                        this.sendStat(true);
                    }
                    if (
                        this.answers.good < this.parameters.amountOfExamples &&
                        !this.parameters.infinityAmountOfExamples
                    ) {
                        this.sendStat(true);
                    }
                } else {
                    this.refreshGameStatBeforeSend();
                    this.sendStat(false);
                    this.wrongImageSubmitHandler();
                }
            },
            async fetchImages() {
                const { images, group } = await LonerBonusGamesApi.getImages({
                    amount: this.parameters.itemsAmount + 1,
                    color: this.parameters.blackWhiteImages ? 'black-white' : 'colored',
                    group: this.pregGroup,
                });
                this.pregGroup = group;
                this.makeCouplesOrTriadsFromImages(images.map(image => this.$options.filters.apiFile(image)));
                this.start();
            },
            makeCouplesOrTriadsFromImages(images) {
                let mode = this.parameters.itemsType.value;
                this.searchedItem = images[0];
                images = images.slice(1);
                images = this.copyItems(images, +mode);
                images.push(this.searchedItem);
                if (mode === '3') {
                    Math.random() > 0.5 && images.push(this.searchedItem);
                }
                this.gameItems = this.shuffleItems(images);
                return images;
            },
            shuffleItems(items) {
                return items.sort(() => 0.5 - Math.random());
            },
            copyItems(imagesArray, n) {
                return imagesArray.reduce((result, element) => [...result, ...Array(n).fill(element)], []);
            },
            initCanvas() {
                this.frame = new Frame('loner', this.gameBox.width, this.gameBox.height, '#f5f5ff', 'black');

                this.frame.on('ready', () => {
                    this.stage = this.frame.stage;
                    this.container = new Container();
                    this.draw();
                    this.soundInstance = this.$createjs.Sound.play('puzzleShake');
                    this.soundInstance.volume = DEFAULT_VOLUME;
                    this.container.addTo(this.stage);
                    this.stage.update();
                });
            },
            draw() {
                this.gameItems.forEach((gameItem, index) => {
                    const assetLoad = this.frame.loadAssets(gameItem);
                    assetLoad.on('complete', () => {
                        this.drawTile(this.frame.asset(gameItem), index, this.searchedItem === gameItem);
                    });
                });
            },
            drawTile(bitmap, counter, isSearchedItem) {
                const randX = rand(
                    this.positionsArray[counter].width,
                    this.gameBox.width - this.positionsArray[counter].width
                );
                const randY = rand(
                    this.positionsArray[counter].height,
                    this.gameBox.height - this.positionsArray[counter].height
                );
                let shape = this.createShape(randX, randY);
                shape.addEventListener('click', () => this.selectImageHandler(bitmap, isSearchedItem, shape));

                this.drawTileShape(shape, bitmap);
                let offsetX = 50;
                let offsetY = 50;
                if (this.isMobile) {
                    offsetX = 5;
                    offsetY = 5;
                }
                if (this.isTablet) {
                    offsetX = 10;
                    offsetY = 10;
                }
                const lastRow = this.positionsArray.slice(-this.tileSize.width);
                const lastItemInRow = this.positionsArray[counter + 1]?.width === 0;
                const itemIsInLastRow = lastRow.some(
                    position => position.height === this.positionsArray[counter].height
                );
                if (lastItemInRow || this.positionsArray[counter].width === 0) {
                    offsetX = 0;
                }
                if (itemIsInLastRow || this.positionsArray[counter].height === 0) {
                    offsetY = 0;
                }
                shape.animate({
                    props: {
                        x: rand(
                            this.positionsArray[counter].width + offsetX,
                            this.positionsArray[counter].width - offsetX
                        ),
                        y: rand(
                            this.positionsArray[counter].height + offsetY,
                            this.positionsArray[counter].height - offsetY
                        ),
                    },
                    time: ANIMATION_TIME,
                });
            },
            drawTileShape(shape, { image }, color = '#EBEBEB') {
                const context = shape.graphics;

                shape.graphics.clear();

                let verticalStep = this.gameBox.height / this.tileSize.height;
                let horizontalStep = this.gameBox.width / this.tileSize.width;

                context.beginBitmapFill(image);

                context.setStrokeStyle(3, 'round');
                context.beginStroke(color);

                const coefficient = 1 / Math.max(image.width / horizontalStep, image.height / verticalStep);

                context.drawRect(0, 0, image.width, image.height);

                context.endFill();

                shape.scaleX = coefficient;
                shape.scaleY = coefficient;
            },
            createShape(x, y) {
                let shape = new Shape();
                shape.drag();
                shape.mouseChildren = false;
                shape.visible = 1;
                shape.pos(x, y);
                shape.addTo(this.container);
                return shape;
            },
            refreshGameStatBeforeSend(lostComplication) {
                if (!this.$offline && (this.user.type === 'student' || this.user.type === 'olympiads-student')) {
                    this.gameStat.attempts = this.answers.good + this.answers.bad;
                    if (lostComplication) {
                        this.gameStat.complicationLost = true;
                    }
                    this.gameStat.answers = this.answers.good;
                    this.gameStat.percent = this.calculatePercent();
                    this.gameStat.isCorrect = this.gameEnd;
                    this.gameStat.parameters = cloneDeep(this.parameters);
                    this.gameStat.parameters.initialItemsAmount = _.clone(this.parametersForLoner?.itemsAmount);
                }
            },
            soundLoad() {
                this.loadedSound++;
                if (this.loadedSound === this.soundObjects.length) {
                    this.isLoading = false;
                    if (this.isFirstTime) {
                        this.isFirstTime = false;
                        this.$nextTick(() => {
                            if ((this.isOlympiad || this.isBattle) && !this.timer) {
                                this.setTimer();
                            }
                        });
                    }
                }
            },
            start() {
                this.initCanvas();
                if (!this.timer) {
                    this.setTimer();
                }
            },
            registerSounds() {
                this.$createjs.Sound.removeAllSounds();
                this.$createjs.Sound.removeAllEventListeners();
                this.$createjs.Sound.addEventListener('fileload', this.soundLoad);
                this.$createjs.Sound.alternateExtensions = ['mp3'];
                this.$createjs.Sound.registerSounds(this.soundObjects);
            },
            showErrorMessage(err) {
                if (err.data) {
                    this.$toasted.error(err.data.message);
                    this.$router.push({ name: 'dashboard' });
                }
                err = err.response && err.response.data ? err.response.data : err;
                if (err && (err.status_code === 403 || err.status_code === 404)) {
                    this.$router.push({ name: 'dashboard' });
                    this.$toasted.error(err.message);
                }
            },
            async getMaxDifficulty() {
                const minImages = await LonerBonusGamesApi.getMinImages();
                this.maxDifficulty = minImages - 1;
            },
        },
        computed: {
            ...mapState({
                user: state => state.auth.user,
                paramsArray: state => state.trainer.paramsArray,
                puzzleGame: state => state.games.puzzle,
                homework: state => state.trainer.homework,
            }),
            courseType() {
                if (this.exercise?.courseType) {
                    return this.exercise.courseType;
                }
                if (this.parameters?.courseType) {
                    return this.parameters.courseType;
                }
                if (this.$store.state.trainer?.course) {
                    return this.$store.state.trainer.course;
                }
                if (this.$route.params?.courseType) {
                    return this.$route.params.courseType;
                }
                return null;
            },
            isHomework() {
                return Boolean(this.homework);
            },
            parametersForLoner() {
                return this.game || this.lonerData;
            },
            possibleToContinueGame() {
                return (
                    (this.parameters.infinityAmountOfExamples ||
                        this.answers.good < this.parameters.amountOfExamples) &&
                    !this.complicationLost
                );
            },
            complicationLost() {
                return this.parameters.complication && this.answers.bad >= 3;
            },
            itemsAmountIsLessThanMax() {
                return this.parameters.itemsAmount < this.maxDifficulty;
            },
            tileSize() {
                const amountOfItems = this.gameItems.length;
                let sides = {
                    width: Math.floor(Math.sqrt(amountOfItems * (this.gameBox.width / this.gameBox.height))),
                    height: 1,
                };
                while (sides.width * sides.height < amountOfItems) {
                    sides.height++;
                }
                return sides;
            },
            isOlympiad() {
                return this.gameType === 'olympiad';
            },
            locale() {
                if (this.user && this.user.locale) {
                    return this.user.locale;
                }
                return 'ru';
            },
            soundObjects() {
                let sounds = [];
                sounds.push(...this.trainerConfig.result);
                sounds.push(...this.trainerConfig.countdownSound);
                sounds.push(...this.trainerConfig.digitsSounds[this.locale]);
                sounds.push(...this.gamesConfig.puzzle.sounds);
                return sounds;
            },
            gameBox() {
                let width = 0;
                let height = 0;
                if (this.documentElementHeight && this.documentElementWidth) {
                    const sizesByDocumentElementWidth = {
                        576: { width: this.documentElementWidth - 40, height: this.documentElementHeight - 50 },
                        768: { width: this.documentElementWidth - 160, height: this.documentElementHeight - 100 },
                        992: { width: this.documentElementWidth - 180, height: this.documentElementHeight - 200 },
                        1440: { width: this.documentElementWidth - 200, height: this.documentElementHeight - 300 },
                    };
                    for (const s in sizesByDocumentElementWidth) {
                        if (this.documentElementWidth < Number(s)) {
                            width = sizesByDocumentElementWidth[s].width;
                            height = sizesByDocumentElementWidth[s].height;
                            break;
                        }
                    }
                    width ||= this.documentElementWidth - 500;
                    height ||= this.documentElementHeight - 250;
                }
                return { width, height };
            },
            positionsArray() {
                const positionsArray = [];
                const horizontalFrameCapacity = this.tileSize.width;
                const verticalFrameCapacity = this.tileSize.height;
                const verticalStep = this.gameBox.height / verticalFrameCapacity;
                const horizontalStep = this.gameBox.width / horizontalFrameCapacity;
                let verticalAlign = 0;
                let horizontalAlign = 0;
                let horizontalStepsCount = 0;
                const amountOfPositions = horizontalFrameCapacity * verticalFrameCapacity;
                while (positionsArray.length < amountOfPositions) {
                    positionsArray.push({ height: verticalAlign, width: horizontalAlign });
                    horizontalAlign += horizontalStep;
                    horizontalStepsCount++;
                    if (horizontalStepsCount === horizontalFrameCapacity) {
                        horizontalAlign = 0;
                        horizontalStepsCount = 0;
                        verticalAlign += verticalStep;
                    }
                }
                return positionsArray;
            },
        },
        watch: {
            'answers.good': {
                handler(val) {
                    if (this.parameters.amountOfExamples === val && !this.parameters.infinityAmountOfExamples) {
                        this.refreshGameStatBeforeSend();
                        this.gameEnd = true;
                        this.sendStat(true);
                    }
                },
                deep: true,
            },
            'answers.bad': {
                handler(val) {
                    if (this.parameters.complication && val >= 3) {
                        this.refreshGameStatBeforeSend(true);
                        this.gameEnd = true;
                        this.sendStat(false);
                    }
                },
                deep: true,
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/sass/games/playbox.scss';

    .play-box {
        height: 100%;
    }
    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }
    .with-warning {
        display: table;
    }
    .loner_game-wrapper {
        display: flex;
        flex-direction: column-reverse;
        width: 100%;
        @include media($xl) {
            flex-direction: row;
        }
    }
    .puzzle-playbox__result-container {
        display: flex;
        flex-direction: row;
        gap: 20px;
        @media screen and (max-width: 1200px) {
            flex-direction: column;
            gap: 20px;
        }
    }
    .loner-game-image {
        border-radius: 5%;
        border: 5px #1d1a1b;
    }
    .answer-button {
        color: $x-color-white;
        background-color: $hsl-color-blue-56;
        align-self: center;
    }
    .loner-game-canvas-handler,
    .sound-loader {
        width: 100%;
        height: 100%;
        text-align: center;
    }
    .puzzle-playbox {
        &_with-loader {
            display: table;
        }
        &__result-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            flex-grow: 1;
            flex-shrink: 1;
        }
        &__result-image {
            @media screen and (max-width: 575px) {
                max-height: 360px;
                max-width: 360px;
            }
            max-width: 450px;
            max-height: 450px;
            min-height: 360px;
            min-width: 360px;
            vertical-align: middle;
            border-radius: 12px;
        }
    }
    .loner-game-canvas-handler_block {
        display: block;
        padding-top: 3%;
    }
    .puzzle-playbox__result-buttons {
        display: flex;
        @media screen and (max-width: 575px) {
            flex-direction: column;
        }
    }
    .play-box__button {
        margin-left: 20px;
        @media screen and (max-width: 575px) {
            margin: 20px 0 0 0;
        }
    }
    .game-info__block {
        display: flex;
        align-items: flex-end;
        flex-direction: row-reverse;
        gap: 15%;
        margin-bottom: 15px;
        @media screen and (min-width: 1440px) {
            display: flex;
            flex-direction: column;
        }
    }

    .loner-playbox__result-image {
        margin-bottom: 20px;
    }
</style>
